import { useEffect } from 'react';

const SITE_NAME = '101Setups';
const SITE_URL = 'https://www.101setups.com';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const STRUCTURED_DATA_SELECTOR = 'script[data-seo-structured-data="true"]';

const ensureMeta = (selector, createTag) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = createTag();
    document.head.appendChild(element);
  }

  return element;
};

const setMetaContent = ({ selector, createTag, content }) => {
  const element = ensureMeta(selector, createTag);
  element.setAttribute('content', content);
};

const setLinkHref = ({ selector, createTag, href }) => {
  const element = ensureMeta(selector, createTag);
  element.setAttribute('href', href);
};

const toAbsoluteUrl = pathOrUrl => {
  if (!pathOrUrl) {
    return SITE_URL;
  }

  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return new URL(pathOrUrl, SITE_URL).toString();
};

const clearStructuredData = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.head.querySelectorAll(STRUCTURED_DATA_SELECTOR).forEach(element => element.remove());
};

const injectStructuredData = (structuredData = []) => {
  if (typeof document === 'undefined') {
    return;
  }

  clearStructuredData();

  structuredData
    .filter(Boolean)
    .forEach(schema => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-structured-data', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
};

export const usePageSeo = ({
  title,
  description,
  canonicalPath,
  image = DEFAULT_IMAGE,
  robots = 'index,follow',
  type = 'website',
  structuredData = [],
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const absoluteUrl = toAbsoluteUrl(canonicalPath || window.location.pathname || '/');
    const absoluteImage = toAbsoluteUrl(image);
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

    document.title = fullTitle;

    setMetaContent({
      selector: 'meta[name="description"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        return meta;
      },
      content: description,
    });

    setMetaContent({
      selector: 'meta[name="robots"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        return meta;
      },
      content: robots,
    });

    setMetaContent({
      selector: 'meta[property="og:title"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:title');
        return meta;
      },
      content: fullTitle,
    });

    setMetaContent({
      selector: 'meta[property="og:description"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:description');
        return meta;
      },
      content: description,
    });

    setMetaContent({
      selector: 'meta[property="og:url"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:url');
        return meta;
      },
      content: absoluteUrl,
    });

    setMetaContent({
      selector: 'meta[property="og:type"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:type');
        return meta;
      },
      content: type,
    });

    setMetaContent({
      selector: 'meta[property="og:image"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image');
        return meta;
      },
      content: absoluteImage,
    });

    setMetaContent({
      selector: 'meta[name="twitter:card"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:card');
        return meta;
      },
      content: 'summary_large_image',
    });

    setMetaContent({
      selector: 'meta[name="twitter:title"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:title');
        return meta;
      },
      content: fullTitle,
    });

    setMetaContent({
      selector: 'meta[name="twitter:description"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:description');
        return meta;
      },
      content: description,
    });

    setMetaContent({
      selector: 'meta[name="twitter:image"]',
      createTag: () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:image');
        return meta;
      },
      content: absoluteImage,
    });

    setLinkHref({
      selector: 'link[rel="canonical"]',
      createTag: () => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        return link;
      },
      href: absoluteUrl,
    });

    injectStructuredData(structuredData);

    return () => {
      clearStructuredData();
    };
  }, [canonicalPath, description, image, robots, structuredData, title, type]);
};

export const seoSiteConfig = {
  siteName: SITE_NAME,
  siteUrl: SITE_URL,
  defaultImage: DEFAULT_IMAGE,
  toAbsoluteUrl,
};
